// Shared bits for the Campaigns v1 pages (brief, launch, dashboard).
import { SiGoogledrive } from "react-icons/si";
import { useState } from "react";
import toast from "react-hot-toast";
import Leftbar from "../../components/LeftBar/lefbar";
import Topbar from "../../components/topbar/Topbar";
import { PLATFORM_META } from "../CampaignsPage/CampaignsPage";
import "./campaigns.css";

export { PLATFORM_META };

// Platforms a campaign can approve for posting (keys match PLATFORM_META).
export const POST_PLATFORMS = ["tiktok", "instagram", "facebook", "youtube", "x"];
export const PLATFORM_POST_LABEL = {
  tiktok: "TikTok",
  instagram: "Instagram Reels",
  facebook: "Facebook Reels",
  youtube: "YouTube Shorts",
  x: "X",
};

export const PLATFORM_FEE_RATE = 0.3; // comes out of clipper payouts; mirrors routes/campaigns.js

export function fmtMoney(n, { cents = false } = {}) {
  const v = Number(n) || 0;
  return `$${v.toLocaleString("en-US", {
    minimumFractionDigits: cents || v % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export const isPool = (c) => c?.payType === "pool";
// A Drive folder holds many videos, so it can't go straight into a clip run.
export const isDriveFolder = (url) => /drive\.google\.com\/(?:drive\/(?:u\/\d+\/)?)?folders\//i.test(String(url || ""));
export const isDriveUrl = (url) => /(?:drive|docs)\.google\.com\//i.test(String(url || ""));
// Google's thumbnail link for a Drive file. Works without sign-in only when the
// file is shared by link; anything else fails to load and shows the Drive logo.
export function driveThumbnail(url) {
  const m = String(url || "").match(/\/file\/d\/([\w-]+)/) || (isDriveUrl(url) && String(url).match(/[?&]id=([\w-]+)/));
  return m ? `https://drive.google.com/thumbnail?id=${m[1]}&sz=w640` : "";
}
// A stream's picture: each source in turn until one loads, then the Drive logo.
export function StreamImage({ url, thumb, placeholderClass }) {
  const [tries, setTries] = useState(0);
  const src = [thumb, driveThumbnail(url)].filter(Boolean)[tries];
  if (src) return <img src={src} alt="" referrerPolicy="no-referrer" onError={() => setTries((n) => n + 1)} />;
  return <span className={placeholderClass}>{isDriveUrl(url) && <SiGoogledrive className="cmp-drive-logo" aria-hidden="true" />}</span>;
}

// "Interview.mov" reads as "Interview". Also covers names saved before this.
export const cleanStreamTitle = (t) => String(t || "").replace(/\.(mov|mp4|m4v|mkv|avi|webm|wmv|flv|mpe?g|mts|m2ts|ts|3gp)$/i, "").trim();
export const fmtUnit = (n) => (n >= 1000000 ? `${+(n / 1000000).toFixed(2)}M` : `${+(n / 1000).toFixed(1)}K`);
// A campaign's pay as the launcher set it, e.g. $100 per 100K views.
export function payRate(c) {
  const unit = Number(c?.payUnit) || 1000;
  const rate = Math.round((Number(c?.payoutPer1K) || 0) * unit / 10) / 100;
  return { rate, unit: fmtUnit(unit), text: fmtMoney(rate, { cents: rate % 1 !== 0 }) };
}
export const fmtShare = (x) => `${(Number(x || 0) * 100).toFixed(x > 0 && x < 0.1 ? 1 : 0)}%`;
// "$1/1K", "$100/100K", or "$10,000 pool".
export function payLabel(c) {
  if (isPool(c)) return `${fmtMoney(c.totalBudget)} pool`;
  const { text, unit } = payRate(c);
  return `${text}/${unit}`;
}

export async function copyText(text, label = "Copied") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.error("Couldn't copy, select it and copy manually.");
  }
}

export function budgetLeft(c) {
  const total = Number(c?.totalBudget) || 0;
  const left = Math.max(0, total - (Number(c?.spentBudget) || 0));
  return { total, left, pct: total > 0 ? Math.min(100, (left / total) * 100) : 0 };
}

export function briefPath(c) {
  return `/c/${c.slug || c._id}`;
}

// Topbar + desktop left bar around a campaigns page.
export function CampaignsShell({ children, wide = false }) {
  return (
    <div className="cmp-layout">
      <Topbar />
      <div className="cmp-main">
        <Leftbar />
        <div className={`cmp-content${wide ? " wide" : ""}`}>{children}</div>
      </div>
    </div>
  );
}

export function CopyButton({ text, label = "Copy", toastLabel }) {
  return (
    <button type="button" className="cmp-copy" onClick={() => copyText(text, toastLabel || "Copied")}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
      {label}
    </button>
  );
}
